import React, { useState } from 'react';
import { Check, X, Shield, DollarSign, TrendingUp, UserPlus, Crown } from 'lucide-react';
import { HiresRangeControl } from './HiresRangeControl';
import { FlipCard } from './FlipCard';
import { BuildFeeCard } from './BuildFeeCard';
import { buildInclusions, monthlyInclusions, salesSchoolInclusion, performanceInclusions, placementOnlyInclusions, setupIncludedNote, leaderFunnelInclusion } from '../data/advantagePricing';
import { usePricing } from '../contexts/PricingContext';
interface AdvantageStructureProps {
  buildFee: number;
  monthlyTotal: number;
  platformFee: number;
  campaignFee: number;
  recruiterFee: number;
  scopeMultiplier: number;
  hasRecruiter: boolean;
  onRecruiterChange: (value: boolean) => void;
  hasLeader: boolean;
  onLeaderChange: (value: boolean) => void;
  leaderBuildFee: number;
  leaderMonthlyFee: number;
  leaderAdSpend: number;
  splitBuildFee: boolean;
  onSplitBuildFeeChange: (value: boolean) => void;
  adSpend: number;
  onAdSpendChange: (value: number) => void;
  cpaLow: number;
  cpaHigh: number;
}
const usd = (value: number) => '$' + value.toLocaleString('en-US');
export function AdvantageStructure({
  buildFee,
  monthlyTotal,
  platformFee,
  campaignFee,
  recruiterFee,
  scopeMultiplier,
  hasRecruiter,
  onRecruiterChange,
  hasLeader,
  onLeaderChange,
  leaderBuildFee,
  leaderMonthlyFee,
  leaderAdSpend,
  splitBuildFee,
  onSplitBuildFeeChange,
  adSpend,
  onAdSpendChange,
  cpaLow,
  cpaHigh
}: AdvantageStructureProps) {
  const [openCards, setOpenCards] = useState({
    build: false,
    monthly: false,
    performance: false
  });
  const toggleCard = (card: 'build' | 'monthly' | 'performance') => setOpenCards(current => ({
    ...current,
    [card]: !current[card]
  }));

  // "Find a leader" switches the cards to the leader campaign, which runs
  // before the standard campaign. The recruiter belongs to the standard
  // campaign, so it is left out of the leader campaign's card pricing (it
  // still flows into the timeline and glance sheet totals).
  const {
    hasImplementationFee,
    hasRetentionBonuses,
    baseBuildFee
  } = usePricing();
  const showRecruiter = hasRecruiter && !hasLeader;
  const implementationFee = hasLeader ? baseBuildFee + leaderBuildFee : buildFee;
  const bonusInclusions = hasRetentionBonuses ? performanceInclusions : hasImplementationFee ? placementOnlyInclusions.filter(line => !/implementation fee/i.test(line)) : placementOnlyInclusions;
  const cardMonthlyTotal = hasLeader ? leaderMonthlyFee : monthlyTotal;
  const cardPipelineFee = hasLeader ? leaderMonthlyFee - platformFee : campaignFee;
  const cardMultiplier = hasLeader ? Math.max(1, Math.ceil(leaderAdSpend / 2500)) : scopeMultiplier;
  const toggleButtonClass = (active: boolean) => `w-full flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-lg text-base font-semibold transition-colors duration-150 ease-out ${active ? 'bg-[#7FE0C3]/15 border border-[#7FE0C3]/40 text-[#7FE0C3] hover:bg-[#7FE0C3]/20' : 'bg-slate-800/50 border border-slate-700 text-slate-300 hover:border-[#7FE0C3]/40 hover:text-white'}`;
  const recruiterButton = <button type="button" onClick={() => onRecruiterChange(!hasRecruiter)} aria-pressed={hasRecruiter} className={toggleButtonClass(hasRecruiter)} data-id="element-40" data-mp="10o28y4vat8ch">
      {hasRecruiter ? <>
          <X className="w-5 h-5" data-id="element-41" data-mp="1tbxzssy6s61c" />
          Remove recruiter
        </> : <>
          <UserPlus className="w-5 h-5" data-id="element-42" data-mp="1yc2kkv64nmxd" />
          Add a recruiter
        </>}
    </button>;
  const leaderButton = <button type="button" onClick={() => onLeaderChange(!hasLeader)} aria-pressed={hasLeader} className={toggleButtonClass(hasLeader)} data-id="element-43" data-mp="1kazy4hb80alx">
      {hasLeader ? <>
          <X className="w-5 h-5" data-id="element-44" data-mp="8vtgdc2lx056" />
          Remove leader search
        </> : <>
          <Crown className="w-5 h-5" data-id="element-45" data-mp="cyzl2fa2mj9j" />
          Find a leader
        </>}
    </button>;
  const leaderTimeframe = <div className="rounded-lg border border-[#7FE0C3]/30 bg-[#7FE0C3]/5 px-3 py-3 text-center" data-id="element-46" data-mp="t7fgsaw78jp6">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-1" data-id="element-47" data-mp="ocjcab9s03v6">
        Average time frame to find a leader
      </div>
      <div className="text-2xl font-bold text-white tracking-tight" data-id="element-48" data-mp="eh7utl6zgijq">
        2–8 weeks
      </div>
    </div>;
  const buildList = (textClass: string) => <>
      {buildInclusions.map(point => <li key={point} className="flex items-start" data-id="element-49" data-mp="1765cbm3debbf">
          <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-50" data-mp="173aorlp28ads" />
          <span className={`${textClass} text-base leading-snug`} data-id="element-51" data-mp="1247oeek0ysc5">{point}</span>
        </li>)}
      {hasLeader && <li className="flex items-start" data-id="element-52" data-mp="3mw9q9j5cfeor">
          <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-53" data-mp="3mxb0jlejbx5f" />
          <span className="text-white text-base font-semibold leading-snug" data-id="element-54" data-mp="3iu4y6oujmq48">
            {leaderFunnelInclusion}
          </span>
        </li>}
    </>;
  return <div data-id="element-55" data-mp="1yasttyybix73">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-56" data-mp="22e7cyyhjztor">
        {/* Implementation */}
        <FlipCard flipped={openCards.build} onToggle={() => toggleCard('build')} label="Implementation" data-id="element-57" data-mp="27dag2ddz3pu3">
          {{
          front: <div className="h-full bg-slate-900/40 border border-slate-800 rounded-xl p-6 flex flex-col" data-id="element-58" data-mp="2c63ll3h9n278">
                <div className="w-11 h-11 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-59" data-mp="2h5g45c13tn40">
                  <Shield className="w-5 h-5 text-[#7FE0C3]" data-id="element-60" data-mp="2q3jseg3h4xzi" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-61" data-mp="2v2mm54e07hs6">
                  Implementation
                </h3>
                <p className="text-slate-400 text-xs mb-5" data-id="element-62" data-mp="vqk3txh48tg3">
                  {hasImplementationFee ? hasLeader ? 'One-time setup, leader campaign' : 'One-time setup' : hasLeader ? 'Setup, leader campaign' : 'Setup and launch'}
                </p>
                <ul className="space-y-2.5" data-id="element-63" data-mp="2d07ifu0cdr8n">{buildList('text-slate-300')}</ul>
                <div className="mt-auto" data-id="element-64" data-mp="2cy340i4th9u4">
                  {!hasLeader && <p className={`pt-4 text-xs leading-snug ${hasRecruiter ? 'text-amber-400' : 'text-slate-500'}`} data-id="element-65" data-mp="2801lbybazcj6">
                      {hasRecruiter ? 'Included: a corporate recruiter aligned to your ICP, comp structure, and hiring standards before launch.' : 'Optional: a corporate recruiter aligned to your ICP and hiring standards before launch.'}
                    </p>}
                  <div className="pt-5" data-id="element-66" data-mp="35pmp67kz4xyr">{leaderButton}</div>
                </div>
              </div>,
          back: <div className="h-full bg-slate-900/60 border border-[#7FE0C3]/30 rounded-xl p-6 flex flex-col" data-id="element-67" data-mp="3kbtvjlnv1d1g">
                <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-3" data-id="element-68" data-mp="3pamxqhxp5hmb">
                  Implementation
                </div>
                {hasImplementationFee ? <BuildFeeCard buildFee={implementationFee} accent="mint" showLabel={false} split={splitBuildFee} onSplitChange={onSplitBuildFeeChange} data-id="element-69" data-mp="3fcqq403yx4sy" /> : <div data-id="element-70" data-mp="36dvgsf5mnelf">
                    <div className="text-4xl font-bold text-white" data-id="element-71" data-mp="2rno9mvl2li01">
                      Included
                    </div>
                    <p className="mt-2 text-sm text-slate-400 leading-snug" data-id="element-72" data-mp="2wn0wvhfusr35">
                      {setupIncludedNote}
                    </p>
                  </div>}
                {hasImplementationFee && hasLeader && <p className="mt-1 text-xs text-slate-500" data-id="element-73" data-mp="2mpw8rc2g6ljp">
                    Includes {usd(leaderBuildFee)} for the leader search.
                  </p>}
                <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-800/50" data-id="element-74" data-mp="2y50z7azewd9j">
                  {buildList('text-slate-400')}
                  {showRecruiter && <li className="flex items-start" data-id="element-75" data-mp="2o5a16zbn7g4p">
                      <Check className="w-5 h-5 text-amber-400 mr-2.5 mt-0.5 shrink-0" data-id="element-76" data-mp="2o8o6afboy3fu" />
                      <span className="text-amber-400 text-base leading-snug" data-id="element-77" data-mp="2k6t0axitf1vq">
                        A corporate recruiter aligned to your ICP, comp
                        structure, and hiring standards before launch
                      </span>
                    </li>}
                </ul>
                <div className="mt-auto pt-5" data-id="element-78" data-mp="20gpp5epk9dus">{leaderButton}</div>
              </div>
        }}
        </FlipCard>

        {/* Monthly Service */}
        <FlipCard flipped={openCards.monthly} onToggle={() => toggleCard('monthly')} label="Monthly Service" data-id="element-79" data-mp="1wfnn6q49enp4">
          {{
          front: <div className="h-full bg-slate-900/40 border border-[#7FE0C3]/30 rounded-xl p-6 relative flex flex-col" data-id="element-80" data-mp="fxdj8kdotcvj">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-xl" data-id="element-81" data-mp="kykpug43g60m" />
                <div className="w-11 h-11 rounded-full bg-[#7FE0C3]/10 flex items-center justify-center mb-4 border border-[#7FE0C3]/20" data-id="element-82" data-mp="pw33gjt239ey">
                  <DollarSign className="w-5 h-5 text-[#7FE0C3]" data-id="element-83" data-mp="3tdbiyx0b9x6i" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-84" data-mp="3tb8mbu4nl5st">
                  Monthly Service
                </h3>
                <p className="text-[#7FE0C3] text-xs mb-5" data-id="element-85" data-mp="6yie7bmdq8tt">
                  {hasLeader ? 'Leader campaign, 2–8 weeks' : 'Recurring, month to month'}
                </p>
                <ul className="space-y-2" data-id="element-86" data-mp="3ambm79mkrj2o">
                  {monthlyInclusions.map(point => <li key={point} className="flex items-start" data-id="element-87" data-mp="16a218smexv74">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-88" data-mp="1b8ddfa06khsi" />
                      <span className="text-slate-300 text-base leading-snug" data-id="element-89" data-mp="1ba6l8smj5823">
                        {point}
                      </span>
                    </li>)}
                  <li className="flex items-start" data-id="element-90" data-mp="1l5rqczmwaog2">
                    <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-91" data-mp="njkdz3kcnrt5" />
                    <span className="text-white text-base font-bold leading-snug" data-id="element-92" data-mp="shcgerimewbl">
                      {salesSchoolInclusion}
                    </span>
                  </li>
                  {showRecruiter && <li className="flex items-start" data-id="element-93" data-mp="127xf0eyao0ui">
                      <Check className="w-5 h-5 text-amber-400 mr-2.5 mt-0.5 shrink-0" data-id="element-94" data-mp="129i8e18vhdqs" />
                      <span className="text-amber-400 text-base leading-snug" data-id="element-95" data-mp="4q6zsa4uw008">
                        Dedicated corporate recruiter owning your pipeline end
                        to end
                      </span>
                    </li>}
                </ul>
                <div className="mt-auto pt-5" data-id="element-96" data-mp="2j0kbsx85dq7a">
                  {hasLeader ? leaderTimeframe : recruiterButton}
                </div>
              </div>,
          back: <div className="h-full bg-slate-900/60 border border-[#7FE0C3]/50 rounded-xl p-6 relative flex flex-col" data-id="element-97" data-mp="291398oo6zsfj">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-xl" data-id="element-98" data-mp="244354jawkzr2" />
                <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-2" data-id="element-99" data-mp="1zas3cwky7960">
                  {hasLeader ? 'Monthly Service · Leader Campaign' : 'Monthly Service'}
                </div>
                <div className="flex items-baseline" data-id="element-100" data-mp="1qd82hoqzzevx">
                  <span className="text-4xl font-bold text-white" data-id="element-101" data-mp="1lctuu7tb66yf">
                    {usd(cardMonthlyTotal)}
                  </span>
                  <span className="ml-2 text-sm text-slate-500" data-id="element-102" data-mp="1gdqwf67u2yzf">/ month</span>
                </div>

                <div className="mt-4 space-y-2 pt-3.5 border-t border-slate-800/50" data-id="element-103" data-mp="16o0igyj6jkfm">
                  <div className="flex justify-between text-sm" data-id="element-104" data-mp="1bphh0ne6yut1">
                    <span className="text-slate-400" data-id="element-105" data-mp="xpf1h7tcwdgy">Partnership</span>
                    <span className="text-white font-medium" data-id="element-106" data-mp="xr9n6ocgc9m3">
                      {usd(platformFee)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-107" data-mp="1ph4dooc1q5cc">
                    <span className="text-slate-400" data-id="element-108" data-mp="1uewhafm1zfwd">
                      Pipeline management{' '}
                      <span className="text-slate-500" data-id="element-109" data-mp="1go110i985wir">
                        ({cardMultiplier}x)
                      </span>
                    </span>
                    <span className="text-white font-medium" data-id="element-110" data-mp="1knjonlsfklpa">
                      {usd(cardPipelineFee)}
                    </span>
                  </div>
                  {showRecruiter && <div className="flex justify-between text-sm" data-id="element-111" data-mp="2d6yol0ct1jty">
                      <span className="text-slate-400" data-id="element-112" data-mp="1pwxn5frl3sn">
                        Corporate recruiter{' '}
                        <span className="text-slate-500" data-id="element-113" data-mp="3svybtmv10dii">
                          ({scopeMultiplier}x)
                        </span>
                      </span>
                      <span className="text-white font-medium" data-id="element-114" data-mp="6homnbj9v1go">
                        {usd(recruiterFee)}
                      </span>
                    </div>}
                  <p className="pt-0.5 text-[11px] text-slate-500 leading-snug" data-id="element-115" data-mp="3ez1klkazwu13">
                    {hasLeader ? `Priced at ${usd(leaderAdSpend)} / mo of ad spend during the search. Once the leader signs, the standard campaign runs at ${usd(monthlyTotal)} / mo on ${usd(adSpend)} of ad spend.` : 'Pipeline management scales per $2,500 of ad spend under management.'}
                  </p>
                </div>

                <ul className="mt-4 space-y-1.5 pt-3.5 border-t border-slate-800/50" data-id="element-116" data-mp="z5apghkoe22h">
                  {monthlyInclusions.map(point => <li key={point} className="flex items-start" data-id="element-117" data-mp="34wpj3apr9ai5">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-118" data-mp="39tzer83qmief" />
                      <span className="text-slate-400 text-base leading-snug" data-id="element-119" data-mp="3du9l4lvfzatt">
                        {point}
                      </span>
                    </li>)}
                  <li className="flex items-start" data-id="element-120" data-mp="3nu0eulc6hlsu">
                    <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-121" data-mp="3nre8uq4xrecq" />
                    <span className="text-white text-base font-bold leading-snug" data-id="element-122" data-mp="3sqh0n8vwno2r">
                      {salesSchoolInclusion}
                    </span>
                  </li>
                  {showRecruiter && <li className="flex items-start" data-id="element-123" data-mp="5h29u00j42rt">
                      <Check className="w-5 h-5 text-amber-400 mr-2.5 mt-0.5 shrink-0" data-id="element-124" data-mp="afnag0jp0ogs" />
                      <span className="text-amber-400 text-base leading-snug" data-id="element-125" data-mp="fegr21ua0vl6">
                        Dedicated corporate recruiter owning your pipeline end
                        to end
                      </span>
                    </li>}
                </ul>

                <div className="mt-auto pt-4 space-y-2.5" data-id="element-126" data-mp="fimrd3y90t6n">
                  {hasLeader ? leaderTimeframe : <>
                      <HiresRangeControl adSpend={adSpend} cpaLow={cpaLow} cpaHigh={cpaHigh} onAdSpendChange={onAdSpendChange} data-id="element-127" data-mp="187txop3y2n2g" />
                      {recruiterButton}
                    </>}
                </div>
              </div>
        }}
        </FlipCard>

        {/* Performance */}
        <FlipCard flipped={openCards.performance} onToggle={() => toggleCard('performance')} label="Performance-Based Pricing" data-id="element-128" data-mp="1qzczism8fv2w">
          {{
          front: <div className="h-full bg-slate-900/40 border border-slate-800 rounded-xl p-6 flex flex-col" data-id="element-129" data-mp="3ggrr39mtvetv">
                <div className="w-11 h-11 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-130" data-mp="3le0ebomu291z">
                  <TrendingUp className="w-5 h-5 text-[#7FE0C3]" data-id="element-131" data-mp="36nt0xnbong85" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-132" data-mp="3bmnjco5qn2rq">
                  Performance-Based Pricing
                </h3>
                <p className="text-slate-400 text-xs mb-5" data-id="element-133" data-mp="32olchrzbpfwj">
                  {hasRetentionBonuses ? 'Earned at retention milestones' : 'Earned when a hire stays active'}
                </p>
                <ul className="space-y-2.5" data-id="element-134" data-mp="2sqnon5ldll4g">
                  {bonusInclusions.map(point => <li key={point} className="flex items-start" data-id="element-135" data-mp="m0pxjl0zqu2y">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-136" data-mp="qz16ltjgp0k4" />
                      <span className="text-slate-300 text-base leading-snug" data-id="element-137" data-mp="2bgadxfg4a031">
                        {point}
                      </span>
                    </li>)}
                </ul>
                <p className="mt-auto pt-4 text-slate-400 text-base leading-snug" data-id="element-138" data-mp="2l63elleq0usi">
                  You pay for the reps you retain, instead of resumés or hires.
                </p>
              </div>,
          back: <div className="h-full bg-slate-900/60 border border-[#7FE0C3]/30 rounded-xl p-6 flex flex-col" data-id="element-139" data-mp="2u5qdo0jz9oie">
                <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-4" data-id="element-140" data-mp="2q4wpd62mi7oz">
                  Performance-Based Pricing
                </div>
                <div className="space-y-4" data-id="element-141" data-mp="2z5mks8nz1w26">
                  <div data-id="element-142" data-mp="2z6nyym0aayo4">
                    <div className="text-white text-sm font-medium mb-1" data-id="element-143" data-mp="2z4bi6c69iucn">
                      Placement bonus
                    </div>
                    <div className="flex items-baseline" data-id="element-144" data-mp="14swq3ops4itq">
                      <span className="text-4xl font-bold text-white" data-id="element-145" data-mp="14ug5ld3m2pug">
                        {usd(500)}
                      </span>
                      <span className="text-slate-400 ml-2 text-sm" data-id="element-146" data-mp="nxivo2obe2vu">
                        per hire
                      </span>
                    </div>
                    <div className="text-slate-500 text-sm mt-1" data-id="element-147" data-mp="squ0jy9kg9l4">
                      {hasRetentionBonuses ? 'Day 15 active in the field' : 'One time, at day 15 active in the field'}
                    </div>
                  </div>
                  {hasRetentionBonuses && <div className="pt-4 border-t border-slate-800/50" data-id="element-148" data-mp="9yj4oodbxbwi">
                    <div className="text-white text-sm font-medium mb-1" data-id="element-149" data-mp="a0ceudn5ie99">
                      Retention bonuses
                    </div>
                    <div className="flex items-baseline" data-id="element-150" data-mp="1ldy0vpxprzqk">
                      <span className="text-4xl font-bold text-white" data-id="element-151" data-mp="1lfiu9c8alcmu">
                        {usd(1000)}
                      </span>
                      <span className="text-slate-400 ml-2 text-sm" data-id="element-152" data-mp="3av1xxc2erisy">
                        per hire
                      </span>
                    </div>
                    <div className="text-slate-500 text-sm mt-1" data-id="element-153" data-mp="35xjyccaa6fxm">
                      Day 45 and day 90 active in the field
                    </div>
                  </div>}
                </div>
                <ul className="mt-5 space-y-2 pt-4 border-t border-slate-800/50" data-id="element-154" data-mp="2w3t9v7e5m5hx">
                  {bonusInclusions.map(point => <li key={point} className="flex items-start" data-id="element-155" data-mp="2n4pqs3ezlq3f">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-156" data-mp="2i5v1qjqff5av" />
                      <span className="text-slate-400 text-base leading-snug" data-id="element-157" data-mp="2i7pl3tm1upcu">
                        {point}
                      </span>
                    </li>)}
                </ul>
                <p className="mt-auto pt-5 text-slate-400 text-base leading-snug" data-id="element-158" data-mp="gc5evhipan1r">
                  Billed only when a hire is still active in the field. You pay
                  for the reps you retain, instead of resumés or hires.
                </p>
              </div>
        }}
        </FlipCard>
      </div>
    </div>;
}